import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';

import { ButtonClickEvent, FormSubmitEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Link } from 'ui/base/link';
import { ToastID, defaultToast } from 'ui/base/toast';
import { BackButton } from 'ui/base/button/back_button';
import { FullStandardPage } from 'ui/base/page/standard_page';

import { DefaultButton, PlainButton } from 'ui/public/button';
import { TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Host, IPangolinDomain, VPNClient } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { getVPNClientStatusText } from '../list_vpn_clients/list_vpn_clients';

import { EditVPNClientStore } from './edit_vpn_client_store';
import Messages from './edit_vpn_client_messages';
import baseStyles from './edit_vpn_client.module.css';

type EditVPNClientStyles = {
  pageContainer: string;
  headerContainer: string;
  saveButton: string;
  section: string;
  inputLabel: string;
  nonceMessage: string;
  qrContainer: string;
  qrGroup: string;
  qrImage: string;
  qrLabel: string;
  nonceContainer: string;
  hidden: string;
  mnemonicContainer: string;
  mnemonicText: string;
  mnemonicHelp: string;
  filler: string;
  hostTitle: string;
  hostRow: string;
  hostColumn: string;
  hostLabel: string;
  hostValue: string;
  hostLinkContainer: string;
  hostLink: string;
  actionsContainer: string;
  action: string;
};

const themedStyles: ThemedStyles<EditVPNClientStyles> = {
  [Theme.DAYLIGHT]: {
    pageContainer: baseStyles.pageContainer,
    headerContainer: baseStyles.headerContainer,
    saveButton: baseStyles.daylightSaveButton,
    section: baseStyles.section,
    inputLabel: baseStyles.daylightInputLabel,
    nonceMessage: baseStyles.daylightNonceMessage,
    nonceContainer: baseStyles.nonceContainer,
    qrContainer: baseStyles.qrContainer,
    qrGroup: baseStyles.qrGroup,
    qrImage: baseStyles.qrImage,
    qrLabel: baseStyles.qrLabel,
    hidden: baseStyles.hidden,
    mnemonicContainer: baseStyles.mnemonicContainer,
    mnemonicText: baseStyles.daylightMnemonicText,
    mnemonicHelp: baseStyles.daylightMnemonicHelp,
    filler: baseStyles.filler,
    hostTitle: baseStyles.daylightHostTitle,
    hostRow: baseStyles.hostRow,
    hostColumn: baseStyles.hostColumn,
    hostLabel: baseStyles.daylightHostLabel,
    hostValue: baseStyles.hostValue,
    hostLinkContainer: baseStyles.hostLinkContainer,
    hostLink: baseStyles.daylightHostLink,
    actionsContainer: baseStyles.actionsContainer,
    action: baseStyles.daylightAction,
  },
  [Theme.MIDNIGHT]: {
    pageContainer: baseStyles.pageContainer,
    headerContainer: baseStyles.headerContainer,
    saveButton: baseStyles.midnightSaveButton,
    section: baseStyles.section,
    inputLabel: baseStyles.midnightInputLabel,
    nonceMessage: baseStyles.midnightNonceMessage,
    nonceContainer: baseStyles.nonceContainer,
    qrContainer: baseStyles.qrContainer,
    qrGroup: baseStyles.qrGroup,
    qrImage: baseStyles.qrImage,
    qrLabel: baseStyles.qrLabel,
    hidden: baseStyles.hidden,
    mnemonicContainer: baseStyles.mnemonicContainer,
    mnemonicText: baseStyles.midnightMnemonicText,
    mnemonicHelp: baseStyles.midnightMnemonicHelp,
    filler: baseStyles.filler,
    hostTitle: baseStyles.midnightHostTitle,
    hostRow: baseStyles.hostRow,
    hostColumn: baseStyles.hostColumn,
    hostLabel: baseStyles.midnightHostLabel,
    hostValue: baseStyles.hostValue,
    hostLinkContainer: baseStyles.hostLinkContainer,
    hostLink: baseStyles.midnightHostLink,
    actionsContainer: baseStyles.actionsContainer,
    action: baseStyles.midnightAction,
  },
};

type NameSectionProps = {
  store: EditVPNClientStore;
};

const NameSection = observer(({ store }: NameSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.section}>
      <div className={styles.inputLabel}>
        {Messages.nameLabel()}
      </div>
      <TextInput store={store.name}/>
      <ErrorMessage message={store.nameError}/>
    </div>
  );
});

type NonceSectionProps = {
  client?: VPNClient;
  store: EditVPNClientStore;
};

const NonceSection = observer(({ client, store }: NonceSectionProps) => {
  const styles = useStyles(themedStyles);

  if (client == null) {
    return (
      <div className={styles.section}>
        <div className={styles.nonceMessage}>
          {Messages.instructions()}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.nonceContainer}>
        {/* QR Code stuff. We do it this way to keep the same height, assuming QR code is taller. */}
        <div className={classNames(styles.qrContainer, !store.qrCodeShow.value ? styles.hidden : null)}>
          <div className={styles.qrGroup}>
            <img
              className={styles.qrImage} src={store.qrCodeData.value}
              alt=''
            />
            <div className={styles.qrLabel}>
              {Messages.qrLabel()}
            </div>
          </div>
        </div>

        {/* Mnemonic stuff */}
        <div className={classNames(styles.mnemonicContainer, store.qrCodeShow.value ? styles.hidden : null)}>
          <div className={styles.mnemonicText} onClick={store.handleCopyQRCode}>
            {client.mnemonic}
          </div>
          <div className={styles.mnemonicHelp} onClick={store.handleCopyQRCode}>
            {Messages.mnemonicHelp()}
          </div>
        </div>
      </div>

      <DefaultButton type='button' onClick={store.toggleNonce}>
        {store.qrCodeShow.value ? Messages.showMnemonic() : Messages.showQR()}
      </DefaultButton>
    </div>
  );
});

type PairedDetailsProps = {
  pangolin: Pangolin;
  host: Host;
  client: VPNClient;
};

const PairedDetails = ({ pangolin, host, client }: PairedDetailsProps) => {
  const styles = useStyles(themedStyles);

  const details: { label: string; value: string }[] = [
    {
      label: Messages.ipAddressLabel(),
      value: host.ip,
    },
    {
      label: Messages.clientStatus(),
      value: getVPNClientStatusText(client),
    },
    {
      label: Messages.connectedToLabel(),
      value: pangolin.nickname,
    },
    {
      label: Messages.lastSeenLabel(),
      value: client.lastSeen == null ? Messages.lastSeenUnknown() : moment(client.lastSeen).fromNow(),
    },
  ];

  return (
    <div className={styles.hostRow}>
      {details.map((item, index) => {
        return (
          <div className={styles.hostColumn} key={index}>
            <div className={styles.hostLabel}>{item.label}</div>
            <div className={styles.hostValue}>{item.value}</div>
          </div>
        );
      })}
      { details.length % 2 === 0 ? null : <div className={styles.hostColumn}></div> }
    </div>
  );
};

type HostSectionProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  client?: VPNClient;
};

const HostSection = observer(({ pangolin, domain, client }: HostSectionProps) => {
  const styles = useStyles(themedStyles);

  if (client?.hostId == null) return null;
  const host = domain.hosts.get(client.hostId);
  if (host == null) return null;

  return (
    <div className={styles.section}>
      <div className={styles.hostTitle}>
        {Messages.hostTitle()}
      </div>
      <PairedDetails pangolin={pangolin} host={host} client={client}/>
      <div className={styles.hostLinkContainer}>
        <Link path={{ kind: BasePath.DEVICES_VIEW, pangolinID: pangolin.id, deviceID: host.id }} className={styles.hostLink}>
          {Messages.goToDevice()}
        </Link>
      </div>
    </div>
  );
});

type ActionSectionProps = {
  pangolin: Pangolin;
  client?: VPNClient;
  store: EditVPNClientStore;
  handleDelete(event: ButtonClickEvent): void;
};

const ActionSection = observer(({ pangolin, client, store, handleDelete }: ActionSectionProps) => {
  const styles = useStyles(themedStyles);

  if (client == null) return null;

  return (
    <div className={styles.actionsContainer}>
      <DefaultButton
        type='button'
        busy={store.busy}
        reserveBusyIconArea={true}
        disabled={!pangolin.onlineStatus}
        className={styles.action}
        onClick={handleDelete}
      >
        {Messages.delete()}
      </DefaultButton>
    </div>
  );
});

type BaseEditVPNClientProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  client?: VPNClient;
};

export const BaseEditVPNClient = observer(({ pangolin, domain, client }: BaseEditVPNClientProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new EditVPNClientStore(client));
  React.useEffect(() => { setStore(new EditVPNClientStore(client)); }, [client]);

  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);

      if (client == null) {
        history.replace(pagePathToPathString({
          kind: BasePath.VPN_EDIT,
          pangolinID: pangolin.id,
          clientID: result.client.id,
        }));
      } else {
        history.goBack();
      }
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, client, domain, pangolin, history]);

  const handleDelete = React.useCallback(async(event: ButtonClickEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleDelete(domain));

    // display toast error messages if the result is not OK
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, domain, history]);

  return (
    <FullStandardPage>
      <form onSubmit={handleSave} className={styles.pageContainer}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton
            type='submit'
            busy={store.busy}
            reserveBusyIconArea={true}
            disabled={!pangolin.onlineStatus}
            className={styles.saveButton}>{Messages.save()}</PlainButton>
        </div>
        <NameSection store={store}/>
        <NonceSection client={client} store={store}/>
        <HostSection
          pangolin={pangolin}
          domain={domain}
          client={client}
        />
        <div className={styles.filler}/>
        <ActionSection
          pangolin={pangolin}
          client={client}
          store={store}
          handleDelete={handleDelete}
        />
      </form>
    </FullStandardPage>
  );
});

export const EditVPNClient = observer(() => {
  const { pangolinID, clientID } = useParams<{ pangolinID: string, clientID: string }>();
  if (!pangolinID) return <Redirect />;
  if (!clientID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  const client = domain.vpnClients.get(clientID);
  if (!client) return <Redirect />;

  return (
    <BaseEditVPNClient
      pangolin={pangolin}
      domain={domain}
      client={client}
    />
  );
});
